import invert from 'lodash/invert';

import {splitFeatureName} from "../../../misc/utils";
import DesignNavigationItem from "../../visComponents/designNavigationItem";
import {Part} from "./part";
import store from '../../../store';
import {LayoutAbbreviation, LayoutType} from "./constants";
import {getFeatureIsLocked} from "../../../misc/helpers";
import {SIDE_POSITIONS} from "../../look/core/constants";

/**
 * Used for Complex parts such as Vanity, to perform some base functionality for related items
 */
export class IntermediatePart extends Part {

  constructor(isChild) {
    super(false, isChild);
    this.disabled = false;
    this.defaultDisabled = false; // used after reset click
    this.canBeDisabled = false;
  }

  /**
   * This will be called on feature creation
   * @param dbFeature
   */
  load(dbFeature) {
    if (this.layerPositions) {
      dbFeature.metaData["layerPosition"] = dbFeature.metaData["Side"] && dbFeature.metaData["Side"][0] !== "0"
        ? dbFeature.metaData["Side"][0]
        : SIDE_POSITIONS[0];
      const partNumber = dbFeature.metaData["layerPosition"] === SIDE_POSITIONS[0] ? 1 : 2;
      const instance = new this.constructor(false, partNumber);
      return this.load_(dbFeature, instance, true, partNumber === 1 ? 'features' : 'featuresParts');
    }
    return this.load_(dbFeature, new this.constructor());
  }

  /**
   * This will be called by the underlying vanity feature with a concrete instance of the part
   * @param dbFeature
   * @param part {Part}
   * @param setParent {boolean}
   * @param features
   */
  load_(dbFeature, part, setParent = true, features = 'features') {

    const featureList = features + "List";

    const {displayName} = dbFeature;
    let {featureName} = splitFeatureName(displayName);
    if (!featureName && dbFeature.groupName && dbFeature.displayName) {
      featureName = dbFeature.displayName.replace(dbFeature.groupName + " - ", "")
    }
    // console.log('displayName:', displayName);

    if (setParent) {
      part.getParent = () => this;
      if (dbFeature.galleryImages) {
        const featured = dbFeature.galleryImages.find(galImage => galImage.featured);
        if (featured) {
          this.featuredImage = featured.url
        }
        dbFeature.galleryImages = dbFeature.galleryImages.sort((a, b) => b.default - a.default)
      }
    }

    if (!dbFeature.sharedFeatureName) {
      console.error("No sharedFeatureName property:", dbFeature);
    }

    part.partName = featureName;
    part.metaData = dbFeature.metaData || {};

    if (dbFeature.s3Bindings && dbFeature.s3Bindings.length) {
      // dbFeature.s3Bindings = dbFeature.s3Bindings.filter(binding => binding.visualizer === "Fireplace")
    }

    part.dbFeature = dbFeature || {};
    if (!part.dbFeature.filterValueIDs) {
      part.dbFeature.filterValueIDs = {}
    }
    part.notAvailableInVisualizer = !dbFeature.s3Bindings || !dbFeature.s3Bindings.length;

    if (part.metaData.RoomLayout) {
      const roomLayout = part.metaData.RoomLayout;
      if (roomLayout.includes(LayoutAbbreviation.All)) {
        Object.keys(LayoutType).forEach(layoutName => {
          this[featureList][layoutName].push(part);
        })
      } else if (Array.isArray(roomLayout) && roomLayout.length) {
        roomLayout.forEach(roomLayoutAbbreviation => {
          const layoutName = invert(LayoutAbbreviation)[roomLayoutAbbreviation];
          if (layoutName) { // Prevent bad roomLayout Metadata
            this[featureList][layoutName].push(part);
          }
        });
      } else {  // single value
        const layoutName = invert(LayoutAbbreviation)[roomLayout];
        if(layoutName) {
          this[featureList][layoutName].push(part);
        }
      }
    }

    if (part.notAvailableInVisualizer || !part.metaData.RoomLayout) {
      part.notAvailableInVisualizer = true;
      Object.keys(LayoutType).forEach(layoutName => {
        this[featureList][layoutName].push(part);
      })
    }

    // Object.keys(LayoutType).forEach(layoutName => {
    //   this[features+"List"][layoutName].push(part);
    // });

    this.loadFeatureCommon(part);

    return part
  }

  getActiveFeatureConditions() {
    return this.getArrayOfConditions()
  }

  // navigationSubItems(filters, searchText, forFeature, filterGroups, firstOpenedFeature, firstOpenedConditions) { // to do
  navigationSubItems(filters, searchText) {
    // const featuresList = (forFeature && forFeature.metaData.layerPosition === SIDE_POSITIONS[1]) ? this.featuresParts : this.features;
    const filteredAvailableFeatures = this.getFeaturesBySearchText(this.getFilteredFeaturesByFilters(filters, this.features), searchText);
    let featuresToDisplay = this.navigationSubItemsWithFilterMultipleConditions(this.getArrayOfConditions(true), filteredAvailableFeatures);
    // console.log("filterGroups", filterGroups);
    return this.returnUniqueNavFeatures(featuresToDisplay)
  }

  navigationSubItemsWithFilter(propName, propValue, featuresToCheck = this.features) {
    return featuresToCheck.filter((feature) => {
      return feature.matchesMetaForCondition(propName, propValue);
    }).map(_ => new DesignNavigationItem(_.featureName, _));
  }

  navigationSubItemsWithFilterMultipleConditions(arrayOfConditions, featuresToCheck = this.features) {
    // console.log('arrayOfConditions', arrayOfConditions);
    // console.log('featuresToCheck', featuresToCheck);
    return featuresToCheck.filter((feature) => {
      return feature.notAvailableInVisualizer || arrayOfConditions.every((conditionObject) => {
        const {propName, propValue} = conditionObject;
        return feature.matchesMetaForCondition(propName, propValue);
      });
    })
  }

  setDefault(layout, setNotInVisFeature = false) {
    this.setFeatureGroupDefault(layout, setNotInVisFeature)
  }

  setLayerPositions = (activeFeature = this.activeFeature) => {
    if (!activeFeature) {
      return
    }
    const layout = this.getParent().layout;
    const conditions = this.getArrayOfConditions();
    if (layout && this.defaultLayerLayoutPositions && this.defaultLayerLayoutPositions[layout.type]) {
      Object.keys(this.layerPositions).forEach(key => {
        if (this.defaultLayerLayoutPositions[layout.type] === key
          && (!this.layerPositions[key] || !this.layerPositions[key].matchesMetaForArrayOfConditions(conditions))) {
          this.layerPositions[key] = activeFeature;
        } else if (activeFeature.dbFeature.metaData["layerPosition"] !== key) {
          this.layerPositions[key] = null;
        }
      })
    } else {
      Object.keys(this.layerPositions).forEach(key => {
        if (layout.initialized) {
          if ((activeFeature.dbFeature.metaData["layerPosition"] === key)
            && (this.layerPositions[key] && !this.layerPositions[key].matchesMetaForArrayOfConditions(conditions))) {
            this.layerPositions[key] = activeFeature;
            // } else if (this.setAllLayerPositions) {
          } else if (this.layerPositions[key]) { // here we have to change vanity cabinet to console for example
            const matchingPositionFeature = this.getMatchingPositionFeature(key);
            if (matchingPositionFeature) {
              this.layerPositions[key] = matchingPositionFeature;
            }
          }
        } else { // first time we should set everything by the default! // missing layers issue
          if ((activeFeature.dbFeature.metaData["layerPosition"] === key)
            && (!this.layerPositions[key] || !this.layerPositions[key].matchesMetaForArrayOfConditions(conditions))) {
            this.layerPositions[key] = activeFeature;
            // } else if (this.setAllLayerPositions) {
          } else {
            const matchingPositionFeature = this.getMatchingPositionFeature(key);
            if (matchingPositionFeature) {
              this.layerPositions[key] = matchingPositionFeature;
            }
          }
        }
      })
    }
  }

  toggleLayerPositions() {
    Object.keys(this.layerPositions).forEach(key => {
      this.layerPositions[key] = this.disabled ? null : this.getMatchingPositionFeature(key)
    })
  }

  getMatchingPositionFeature = (position = "", fromFeature = this.activeFeature, fromLayout = null) => {
    if (fromFeature) { //just in case
      const name1 = fromFeature.getSplittedFeatureName();
      let arrayOfConditions = this.getArrayOfConditions();
      if (fromFeature.metaData["SkuName"]) {
        arrayOfConditions.push({
          propName: "SkuName",
          propValue: fromFeature.metaData["SkuName"]
        })
      }
      if (fromLayout) {
        let roomLayoutMeta = arrayOfConditions.find(condition => condition.propName === "RoomLayout");
        let roomSceneMeta = arrayOfConditions.find(condition => condition.propName === "RoomScene");
        if (roomLayoutMeta) {
          roomLayoutMeta.propValue = fromLayout.getCurrentLayoutAbbreviation();
        }
        if (roomSceneMeta) {
          roomSceneMeta.propValue = fromLayout.scene;
        }
      }
      const skuNamesPartIds = ["shower_door", "walk_in_tub", "shower_pan_combo"];
      const whereToFind = (!this.layerPositions || (position === Object.keys(this.layerPositions)[0])) ? this.features : this.featuresParts;
      return whereToFind.find((feature) => {
        return (skuNamesPartIds.includes(this.partId) || name1 === feature.getSplittedFeatureName())
          && feature.matchesMetaForArrayOfConditions(arrayOfConditions) && feature.metaData["layerPosition"] === position
      });
    }
    return null
  };

  getArrayOfConditions() {
    const layout = this.getParent().layout;
    return [{
      propName: 'RoomLayout',
      propValue: layout.getCurrentLayoutAbbreviation()
    }, {
      propName: 'RoomScene',
      propValue: layout.scene
    }];
  }

  setLayers(layout) {
    if (this.activeFeature && (this.activeFeature.matchesMetaForArrayOfConditions(this.getArrayOfConditions()))) {
      if (this.hasAdditionalParts) {
        this.checkIfFeaturePartIsMatching();
      }
      return this._setLayers(layout);
    }
    this.setDefault(layout);
    return this._setLayers(layout);
  }
  showLayerPositionsModal() {
    return false
  }

  clickFromAllWalls(ignoreLocked) {
    if (this && this.dbFeature && this.dbFeature.sharedFeatureName) {
      console.log(`${this.partId}:`, this.dbFeature.sharedFeatureName);
    }
    this.handleClick(ignoreLocked, "all_walls");
  }

  tryToSetTheSameFeature(filteredFeatures = [], partName = "") {
    if (this.activeFeature && this.activeFeature.partName) {
      let splittedFromTheLayoutName = partName
        ? this.removeLayoutFromPartName(partName)
        : this.activeFeature.getMatchingFeatureName();
      this.activeFeature = filteredFeatures.find(feature => {
        return feature.getMatchingFeatureName() === splittedFromTheLayoutName
      });
      if (this.activeFeature) {
        return true
      }
    }
    return false
  }

  /**
   * @param ignoreLocked {boolean}  // false by the default, is used by wizard ONLY
   */
  handleClick(ignoreLocked = false) {
    const parent = this.getParent();
    const {activeQuoteIndex} = store.getState().quotesState;
    parent.firstClick[activeQuoteIndex] = true; // allow to show active item
    const featureIsLocked = getFeatureIsLocked(this, ignoreLocked);
    if (featureIsLocked) {
      parent.activeLockedFeature = this;
    } else {
      if (parent.activeLockedFeature) {
        parent.activeLockedFeature = null;
      }
      this.featureClick();
    }
  }

  hideLayers() {
    if (this.activeFeature) {
      this.activeFeature.layer.src = '';
    }
    if (Array.isArray(this.activeFeatureParts) && this.activeFeatureParts.length) {
      this.activeFeatureParts.forEach((activeFeaturePart) => {
        if (activeFeaturePart && activeFeaturePart.layer) {
          activeFeaturePart.layer.src = '';
        }
      })
    }
    if (this.placeholderFeature) {
      this.placeholderFeature.layer.src = '';
    }
    if (this.layerPositions) {
      Object.keys(this.layerPositions).forEach(key => {
        if (this.layerPositions[key]) {
          this.layerPositions[key].layer.src = '';
        }
      })
    }
  }

  setZIndex(zIndexes) {

    const getZIndex = (feature, partId = null) => {
      return feature.metaData["ZSorting"] ? feature.metaData["ZSorting"] * 1000 : zIndexes[partId || this.partId];
    }

    if (this.layerPositions) {
      Object.keys(this.layerPositions).forEach((key, keyIndex) => {
        const feature = this.layerPositions[key];
        if (feature) {
          feature.layer.zIndex = getZIndex(feature) + keyIndex*10;
        }
      })
    } else if (zIndexes && this.activeFeature) {
      this.activeFeature.layer.zIndex = getZIndex(this.activeFeature);
      if (this.hasAdditionalParts && this.activeFeatureParts && this.activeFeatureParts.length) {
        this.activeFeatureParts.forEach((activeFeaturePart, keyIndex) => {
          activeFeaturePart.layer.zIndex = getZIndex(activeFeaturePart, this.additionalPartPartId) + (keyIndex + 1) * 10;
        });
      }
    }
  }

  removeEdgeFromPartName(partName) {
    const filterGroups = store.getState().filterState.filterGroups;
    const edgeFilterGroup = filterGroups.find(group => group.name === "Edge" || group.name === "Edge Profile");
    if (edgeFilterGroup && edgeFilterGroup.values && edgeFilterGroup.values.length) {
      const edgeFilterGroupValues = edgeFilterGroup.values.sort((a,b) => {
        return a.value && b.value && (b.value.length - a.value.length)
      }); // 1st should be "Full Bullnose", then simple "Bullnose"
      edgeFilterGroupValues.forEach((filterGroupValue) => { // arrayOf {id: 293, value: "Eased", displayText: "Eased", parent_id: null, icon: "", …}
        partName = partName.split(" "+filterGroupValue.value).join("");
      });
    }
    return partName
  }

  getCountertopGroupsSplittedFeatureName = () => {
    return this.getSplittedFeatureName()
  };

  getSplittedFeatureName = () => {
    if (this._customDisplayName == null) {
      this._customDisplayName = this.dbFeature.sharedFeatureName || this.partName || "";
    }
    return this._customDisplayName
  };

  getMatchingFeatureName = () => {
    return this.getSplittedFeatureName()
  };

  getModuleFeatureName = () => {
    // return this.partName.split(" M_")[0];
    return this.partName.split(" M")[0];
  };

  featureIsMatchingFilters(currentFeature, activeFilters, filters = []) {
    return filters.every(filter => {
      return activeFilters[filter.id]
        && currentFeature.dbFeature.filterValueIDs[filter.id]
        && currentFeature.dbFeature.filterValueIDs[filter.id].includes(activeFilters[filter.id][0])
    })
  }

  getActiveFeaturePartsAccordingToActiveFeature(fromFeature = this.activeFeature, activeFeatureConditions = []) {
    let activeFeatureParts = [];
    if (fromFeature && fromFeature.partNumber) {
      let activeFeaturePart;
      const name1 = fromFeature.getSplittedFeatureName();
      // console.log("name1", name1);
      let arrayOfConditions = activeFeatureConditions.length
        ? activeFeatureConditions
        : this.getActiveFeatureConditions(fromFeature);
      // if (this.partId === "right_wall") {
      //   arrayOfConditions = arrayOfConditions.filter(condition => condition.propName !== "MirrorSize");
      // }
      if (this.getAdditionalPartsConditions) {
        arrayOfConditions = this.getAdditionalPartsConditions(arrayOfConditions); // modify or extend parts conditions
      }
      activeFeatureParts = this.featuresParts.filter((feature) => name1 === feature.getSplittedFeatureName());
      // console.log("this.featuresParts", this.featuresParts);
      // console.log("activeFeatureParts", activeFeatureParts);
      if (activeFeatureParts.length) {
        activeFeaturePart = activeFeatureParts.find((feature) => {
          return feature.matchesMetaForArrayOfConditions(arrayOfConditions)
        });
        if (!activeFeaturePart) {
          activeFeaturePart = activeFeatureParts[0];
        }
      }
      activeFeatureParts = activeFeaturePart ? [activeFeaturePart] : [];
      activeFeatureParts.forEach((activeFeaturePart) => {
        activeFeaturePart.disabled = this.disabled
      });
    }
    return activeFeatureParts
  }

  checkIfFeaturePartIsMatching() {
    if (this.activeFeatureParts && this.activeFeatureParts.length) {
      const activeFeatureConditions = this.getActiveFeatureConditions();
      const name1 = this.activeFeature.getSplittedFeatureName();
      const activeFeaturePart = this.activeFeatureParts[0];
      if (!(activeFeaturePart.matchesMetaForArrayOfConditions(activeFeatureConditions)
        && name1 === activeFeaturePart.getSplittedFeatureName())) {
        this.activeFeatureParts = this.setActiveFeatureParts(this.activeFeature, activeFeatureConditions);
      }
    } else {
      this.activeFeatureParts = this.setActiveFeatureParts();
    }
  }

  setActiveFeatureParts(fromFeature = this.activeFeature, activeFeatureConditions = []) {
    return this.getActiveFeaturePartsAccordingToActiveFeature(fromFeature, activeFeatureConditions);
  }

}
